import { FileClockIcon, RotateCcwIcon } from "lucide-react";

import type { IChatRecoveryStatus } from "@/api/chat/chat.types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    hasReachedHorizon,
    shouldExposeRetry,
    isRecoveryTerminal,
} from "@/hooks/chat/useMessageRecoveryStatus";

import {
    DocumentWaitCancelButton,
    type DocumentWaitCancelOutcome,
} from "./DocumentWaitCancelButton";
import {
    type DocumentWaitCounts,
    DOCUMENT_WAIT_NOTE,
    documentWaitBody,
    documentWaitCounts,
    documentWaitTitle,
    isDocumentWait,
} from "./document-wait";

export type RecoveringMessageProps = {
    status: IChatRecoveryStatus | undefined;
    takingLonger: boolean;
    /** Omitted while no retry request exists; the copy then carries the way out. */
    onRetry?: () => void;
    /** Omitted while no abandon request can be honoured; no handler, no control. */
    onCancel?: () => Promise<DocumentWaitCancelOutcome>;
    /**
     * The imported message says this turn is parked. It fills the gap between
     * import and the first poll and is superseded by any status that arrives,
     * so it can only ever show the card early -- never keep it up.
     */
    waitingHint?: boolean;
};

// Staggered so the three dots read as a wave rather than a blink. Literal
// classes: Tailwind generates arbitrary values only from source it can scan.
const DOT_DELAY = ["", "[animation-delay:200ms]", "[animation-delay:400ms]"];

function PulseDots() {
    return (
        <span
            data-slot="recovery-dots"
            className="flex items-center gap-1"
            aria-hidden="true"
        >
            {DOT_DELAY.map((delay, index) => (
                <span
                    key={index}
                    className={`bg-primary/70 size-1.5 animate-pulse rounded-full [animation-duration:1.2s] motion-reduce:animate-none ${delay}`}
                />
            ))}
        </span>
    );
}

type DocumentWaitCardProps = {
    /**
     * Null before the first poll, and whenever the server's pair disagrees.
     * It selects the singular wording and nothing else reads it.
     */
    counts: DocumentWaitCounts | null;
    onCancel?: () => Promise<DocumentWaitCancelOutcome>;
};

/**
 * The parked-turn notice.
 *
 * Nothing is being written yet, so it borrows the primary-tinted notice idiom
 * (ContextEnrichmentCard) rather than the writing dots, and stays clear of the
 * destructive error panel. It carries no meter and no count: a bar is a
 * watch-me affordance, and this wait is answered by a server sweep whether or
 * not anyone is looking. The release line gets its own block at full
 * foreground weight -- it is what someone scanning the card needs, and an
 * inline emphasis span strands mid-line in the narrow thread column.
 */
function DocumentWaitCard({ counts, onCancel }: DocumentWaitCardProps) {
    return (
        <Card
            data-slot="document-wait"
            size="sm"
            className="aui-document-wait fade-in animate-in bg-primary/[0.035] ring-primary/15 dark:bg-primary/[0.08] dark:ring-primary/25 mt-3 w-full max-w-lg gap-3 duration-200"
        >
            <CardContent className="flex flex-col gap-3">
                <div
                    data-slot="document-wait-copy"
                    role="status"
                    aria-live="polite"
                    className="flex items-start gap-3"
                >
                    <span
                        data-slot="document-wait-icon"
                        aria-hidden="true"
                        className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg"
                    >
                        <FileClockIcon className="size-4.5 animate-pulse [animation-duration:2.4s] motion-reduce:animate-none" />
                    </span>
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <p className="text-foreground text-sm font-medium">
                            {documentWaitTitle(counts)}
                        </p>
                        <p className="text-muted-foreground text-sm text-pretty">
                            {documentWaitBody(counts)}
                        </p>
                        <p className="text-foreground mt-1 text-sm text-pretty">
                            {DOCUMENT_WAIT_NOTE}
                        </p>
                    </div>
                </div>
                {onCancel !== undefined && (
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        <DocumentWaitCancelButton onCancel={onCancel} />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * The affordance under a turn the server is still finishing after a disconnect.
 *
 * The recovery states are deliberately chromeless: a bordered, tinted panel
 * would make a recovering answer rhyme with a failed one, so they sit on the
 * message's own left edge and read as the prose continuing. The document wait
 * is the one exception -- nothing is being written yet, so it borrows the
 * primary-tinted notice idiom (ContextEnrichmentCard) rather than the writing
 * dots, and stays clear of the destructive error panel.
 */
export function RecoveringMessage({
    status,
    takingLonger,
    onRetry,
    onCancel,
    waitingHint,
}: RecoveringMessageProps) {
    if (status === undefined) {
        // No counts and no Cancel: both need polled data, and a control that
        // cannot name a turn yet is worse than none. Any status supersedes
        // this, including one that says the turn is not waiting at all.
        return waitingHint === true ? <DocumentWaitCard counts={null} /> : null;
    }

    const canRetry = onRetry !== undefined && shouldExposeRetry(status);

    // Ghost like the action-bar buttons, so retry reads as a message action
    // rather than a standalone control the thread has no other example of.
    const retryButton = canRetry ? (
        <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-foreground -my-1 h-7 px-2"
            onClick={onRetry}
        >
            <RotateCcwIcon data-icon="inline-start" className="size-3.5" />
            Try again
        </Button>
    ) : null;

    if (isRecoveryTerminal(status)) {
        // A terminal turn's explanation belongs to the thread's error rendering
        // and its retry to the action bar (RecoveryRetryButton), where every
        // other message action lives.
        return null;
    }

    if (isDocumentWait(status)) {
        // A park is bounded by the server's own stored deadline, not by the
        // recovery horizon, so neither a stray retry authorisation nor a spent
        // client poll budget belongs on a wait that is running as designed.
        return (
            <DocumentWaitCard
                counts={documentWaitCounts(status)}
                onCancel={onCancel}
            />
        );
    }

    if (hasReachedHorizon(status)) {
        // Still projected as recovering, so the thread has no error rendering to
        // show. Say plainly that it stopped, and name the way out -- the composer
        // is right there even before the retry request exists.
        return (
            <div
                role="status"
                aria-live="polite"
                className="aui-recovering-stalled text-muted-foreground fade-in animate-in mt-2 flex flex-wrap items-center gap-2 text-sm duration-200"
            >
                <span>
                    This answer stopped before it finished.
                    {retryButton === null && " Ask again to get a fresh one."}
                </span>
                {retryButton}
            </div>
        );
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="aui-recovering-active fade-in animate-in text-muted-foreground mt-2 flex items-center gap-2 text-sm duration-200"
        >
            <PulseDots />
            <span>
                Finishing your response…
                {takingLonger && (
                    <span className="text-muted-foreground/80">
                        {" "}
                        Taking longer than expected — it will finish on its own.
                    </span>
                )}
            </span>
        </div>
    );
}
